import React, { useState, useMemo, useEffect } from "react";
import BackButton from "../base/BackButton";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { format } from "date-fns";
import moment from "moment";
import momentTz from "moment-timezone";
import { FileExcelOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";

function AttendanceReport() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const getInitialDates = () => {
    const today = new Date();
    const last7Days = new Date(today);

    last7Days.setDate(today.getDate() - 6);

    return {
      start: formatDate(last7Days),
      end: formatDate(today),
    };
  };

  const initialDates = useMemo(() => getInitialDates(), []);

  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("late");
  const [filterKey, setFilterKey] = useState(0);

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  const getBranches = async () => {
    try {
      const response = await apiUrl.get("/getBranches");
      if (response?.status) {
        setBranches(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getBranches();
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [filterKey]);

  const handleClear = () => {
    const defaults = getInitialDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
    setStatus("late");
    setSelectedBranch("all");
    setFilterKey((prev) => prev + 1);
  };
  const handleApply = () => {
    setFilterKey((prev) => prev + 1);
  };

  const fetchAttendanceData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start: startDate,
        end: endDate,
        status: status,
      });

      if (selectedBranch !== "all") {
        params.append("branch_id", selectedBranch);
      }

      const response = await apiUrl.get(
        `attendance/report?${params.toString()}`,
      );

      if (response?.data?.data) {
        setAttendanceData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    // Check if data exists
    if (!attendanceData || attendanceData.length === 0) {
      Swal.fire({
        title: "No data to export",
        text: "Please apply filters to load attendance records first.",
        icon: "info",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    try {
      // Dynamically import Excel libraries (reduces initial bundle size)
      const XLSX = await import("xlsx");
      const FileSaver = await import("file-saver");

      // Format date range for filename
      const startDateFormatted = format(new Date(startDate), "yyyy-MM-dd");
      const endDateFormatted = format(new Date(endDate), "yyyy-MM-dd");
      const statusLabel = status === "late" ? "Late" : "Absent";
      const branchLabel =
        selectedBranch === "all" ? "All_Branches" : `Branch_${selectedBranch}`;

      // Prepare Excel rows
      const rows = [];

      // Title & Metadata
      rows.push([`Attendance Report - ${statusLabel} Status`]);
      rows.push([`Date Range: ${startDateFormatted} to ${endDateFormatted}`]);
      rows.push([
        `Branch: ${selectedBranch === "all" ? "All Branches" : branches.find((b) => (b.id || b._id) == selectedBranch)?.name || selectedBranch}`,
      ]);
      rows.push([]); // Empty row

      // Header Row
      rows.push([
        "User Name",
        "Email",
        "Date",
        "Check In (Local)",
        "Check Out (Local)",
        "Status",
        "Working Hours",
        "Branch",
      ]);

      // Data Rows
      attendanceData.forEach((record) => {
        // Format Check In/Out with user's timezone
        const formatLocalTime = (datetime, timeZone) => {
          if (!datetime || !timeZone) return "--:--";
          try {
            return momentTz
              .utc(datetime)
              .tz(timeZone)
              .format("YYYY-MM-DD HH:mm");
          } catch {
            return moment(datetime).format("YYYY-MM-DD HH:mm");
          }
        };

        rows.push([
          record.user_name || "—",
          record.email || "—",
          record.attendance_date
            ? format(new Date(record.attendance_date), "MMM dd, yyyy (EEEE)")
            : "—",
          formatLocalTime(record.check_in, record.userBranchTimeZone),
          formatLocalTime(record.check_out, record.userBranchTimeZone),
          record.display_status || record.attendance_status || "—",
          record.worked_hours_formatted || "0m",
          record.branch_name || "—",
        ]);
      });

      // Create Worksheet
      const ws = XLSX.utils.aoa_to_sheet(rows);

      // Style Header Row (Row 5 = index 4, 1-based = 5)
      const headerRow = 5;
      const headerCols = ["A", "B", "C", "D", "E", "F", "G", "H"];

      headerCols.forEach((col) => {
        const cellRef = `${col}${headerRow}`;
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true, color: { rgb: "FFFFFFFF" } },
            fill: { fgColor: { rgb: "FF2E7D32" } }, // Green header
            alignment: { horizontal: "center", vertical: "middle" },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
            },
          };
        }
      });

      // Style Title Row
      if (ws["A1"]) {
        ws["A1"].s = {
          font: { bold: true, sz: 14, color: { rgb: "FF1565C0" } },
          alignment: { horizontal: "left" },
        };
      }
      if (ws["A2"]) {
        ws["A2"].s = {
          font: { bold: true, sz: 11, color: { rgb: "FF424242" } },
          alignment: { horizontal: "left" },
        };
      }
      if (ws["A3"]) {
        ws["A3"].s = {
          font: { sz: 10, color: { rgb: "FF616161" } },
          alignment: { horizontal: "left" },
        };
      }

      // Set Column Widths
      ws["!cols"] = [
        { wch: 25 }, // User Name
        { wch: 30 }, // Email
        { wch: 22 }, // Date
        { wch: 20 }, // Check In
        { wch: 20 }, // Check Out
        { wch: 18 }, // Status
        { wch: 15 }, // Working Hours
        { wch: 30 }, // Branch
      ];

      // Auto-fit rows (basic)
      ws["!rows"] = [{ hpx: 20 }, { hpx: 20 }, { hpx: 20 }, { hpx: 10 }]; // First 4 rows

      // Create Workbook & Export
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `Attendance_Report_${statusLabel}_${startDateFormatted}_to_${endDateFormatted}_${branchLabel}.xlsx`;
      FileSaver.saveAs(blob, fileName);

      // Success notification
      Swal.fire({
        title: "Export Successful!",
        text: `Downloaded ${attendanceData.length} records.`,
        icon: "success",
        toast: true,
        position: "top-end",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Excel export error:", error);
      Swal.fire({
        title: "Export Failed",
        text: "Could not generate Excel file. Please try again.",
        icon: "error",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* header */}
      <header className="mb-4">
        <div className="flex items-center gap-3">
          <BackButton url="/attendence" />
          <h1 className="text-xl font-semibold text-gray-800">
            Attendance Report
          </h1>
        </div>
      </header>
      {/* filter section */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
        <h3 className="text-sm font-semibold text-gray-600 mb-4">
          Filter Attendance
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              Branch
            </label>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Branches</option>
              {branches.map((branch) => (
                <option
                  key={branch.id || branch._id}
                  value={branch.id || branch._id}
                >
                  {branch.name || branch.branch_name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-600 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="late">Late</option>
              <option value="absent">Absent</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end items-end gap-2 mt-6">
          <button onClick={handleApply} className="btn btn-primary">
            Apply Filter
          </button>

          <button onClick={handleClear} className="btn btn-light">
            Clear All
          </button>
        </div>
      </div>
      {/* export excel */}
      <div className="mt-6 flex items-center justify-end">
        <button
          className="btn btn-success btn-lg"
          disabled={loading || attendanceData?.length === 0}
          onClick={exportToExcel}
        >
          <FileExcelOutlined className="me-1" />
          Export Excel
        </button>
      </div>
      {/* Attendance Data Table */}
      <div className="mt-8">
        {/* Loading Skeleton */}
        {loading && (
          <div className="overflow-hidden border border-gray-200 rounded-xl">
            <div className="animate-pulse">
              {/* Header Skeleton - 7 columns */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="grid grid-cols-7 gap-4">
                  {[...Array(7)].map((_, i) => (
                    <div
                      key={i}
                      className="h-4 bg-gray-200 rounded w-3/4"
                    ></div>
                  ))}
                </div>
              </div>
              {/* Row Skeletons - 7 columns */}
              {[...Array(5)].map((_, rowIndex) => (
                <div
                  key={rowIndex}
                  className="px-6 py-4 border-b border-gray-100 last:border-0"
                >
                  <div className="grid grid-cols-7 gap-4">
                    {[...Array(7)].map((_, colIndex) => (
                      <div
                        key={colIndex}
                        className="h-4 bg-gray-100 rounded w-full"
                      ></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && attendanceData.length === 0 && (
          <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-xl">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="mt-4 text-gray-600 font-medium">No records found</p>
            <p className="text-sm text-gray-400">Try adjusting your filters</p>
          </div>
        )}

        {/* Data Table - 7 Columns */}
        {!loading && attendanceData.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date
                  </th>

                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Check Out
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Working Hours
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {attendanceData.map((record, index) => (
                  <tr
                    key={record.attendance_id || record.user_id || index}
                    className="hover:bg-gray-50 transition"
                  >
                    {/* 2. Name + Email */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                          {record.user_name?.charAt(0) || "U"}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {record.user_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {record.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* 1. Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.attendance_date
                        ? format(
                            new Date(record.attendance_date),
                            "MMM dd, yyyy (EEEE)",
                          )
                        : "—"}
                    </td>

                    {/* 3. Check In Time */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.check_in
                        ? momentTz
                            .utc(record.check_in)
                            .tz(record.userBranchTimeZone)
                            .format("YYYY-MM-DD HH:mm")
                        : "--:--"}
                    </td>

                    {/* 4. Check Out Time */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.check_out
                        ? momentTz
                            .utc(record.check_out)
                            .tz(record.userBranchTimeZone)
                            .format("YYYY-MM-DD HH:mm")
                        : "--:--"}
                    </td>

                    {/* 5. Status Badge */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          record.display_status === "Late Arrival"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {record.display_status}
                      </span>
                    </td>

                    {/* 6. Working Hours */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {record.worked_hours_formatted || "0m"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttendanceReport;
